/**
 * GegevensKaart: "Je gegevens" op Mijn Kleda, met zelf wijzigen (plan
 * 2026-10-28, golf 2).
 *
 *   Telefoon   altijd (ook via de link in een mail): het mobiele nummer.
 *   Adres      "Alleen voor deze bestelling" zolang de bestelling nog gepland
 *              moet worden of ingepland staat; "Ik ben verhuisd" alleen
 *              ingelogd (dan wijzigt ook de klantkaart en lopen de orders mee).
 *   E-mail     alleen ingelogd: we sturen een bevestigingsmail naar het nieuwe
 *              adres; tot de klik blijft het oude staan.
 *
 * De server beslist wat mag (data.gegevens); zonder `wijzigen` is de kaart
 * alleen-lezen zoals voorheen. Na een wijziging laadt de ouder de stand
 * opnieuw en toont de melding.
 */
import { useState } from 'react'
import { KLEDA_CONTACT } from '@platform/kleda-content'
import { postMijnKleda, type MijnKledaAdres, type MijnKledaData } from '../../api'
import { trackGegevensGewijzigd } from '../../analytics'
import { Card } from '../UI'
import { AdresVelden, useAdresForm } from '../AdresVelden'

const INLOG_AAN = import.meta.env.VITE_MIJN_KLEDA_INLOG_ENABLED === 'true'
const KNOP = 'rounded-xl bg-ink px-md py-sm text-body-sm font-semibold text-white disabled:opacity-40'

type Veld = 'adres' | 'telefoon' | 'email'

function adresRegel(a: MijnKledaAdres | null | undefined): string {
  if (!a?.straat) return ''
  return `${a.straat}${a.toevoeging && !a.straat.endsWith(a.toevoeging) ? ` ${a.toevoeging}` : ''}, ${[a.postcode, a.plaats].filter(Boolean).join(' ')}`
}

function WijzigLink({ onClick }: { onClick: () => void }) {
  return <button type="button" onClick={onClick} className="ml-sm text-[13px] font-medium text-primary-dark underline">Wijzigen</button>
}

function Knoppen({ bezig, onAnnuleer, label = 'Opslaan' }: { bezig: boolean; onAnnuleer: () => void; label?: string }) {
  return (
    <div className="mt-sm flex flex-wrap gap-sm">
      <button type="submit" disabled={bezig} className={KNOP}>{bezig ? 'Even geduld...' : label}</button>
      <button type="button" disabled={bezig} onClick={onAnnuleer} className="text-body-sm text-ink-muted underline">Annuleren</button>
    </div>
  )
}

function AdresBewerken({ token, data, onKlaar, onAnnuleer }: { token: string; data: MijnKledaData; onKlaar: (melding: string, scope: string) => void; onAnnuleer: () => void }) {
  const g = data.gegevens!
  const [scope, setScope] = useState<'order' | 'klantkaart'>(g.orderAdres ? 'order' : 'klantkaart')
  const adres = useAdresForm(adresRegel(g.orderAdres ?? g.klantkaartAdres) || data.klant.adres || '')
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)

  const opslaan = async () => {
    if (!adres.isGewijzigd()) return onAnnuleer()
    const f = adres.valideer()
    if (f) return setFout(f)
    setBezig(true)
    setFout(null)
    const r = await postMijnKleda({ token, actie: 'adres', scope, ...adres.delen() })
    setBezig(false)
    if (r.success && r.data && r.data.actie === 'adres') return onKlaar(r.data.melding, scope)
    setFout(r.error?.message || 'Dat lukte niet. Probeer het zo opnieuw of bel ons.')
  }

  return (
    <form className="mt-sm space-y-sm rounded-xl bg-surface-soft p-sm" onSubmit={(e) => { e.preventDefault(); void opslaan() }}>
      <fieldset className="space-y-xs">
        {g.orderAdres && (
          <label className="flex items-start gap-xs text-body-sm text-ink">
            <input type="radio" name="mk-adres-scope" checked={scope === 'order'} onChange={() => setScope('order')} className="mt-[3px]" />
            <span>Alleen voor deze bestelling <span className="text-ink-faint">({g.orderAdres.kant === 'ophalen' ? 'hier halen we je kleed op' : 'hier brengen we je kleed terug'})</span></span>
          </label>
        )}
        {g.klantkaartAdres ? (
          <label className="flex items-start gap-xs text-body-sm text-ink">
            <input type="radio" name="mk-adres-scope" checked={scope === 'klantkaart'} onChange={() => setScope('klantkaart')} className="mt-[3px]" />
            <span>Ik ben verhuisd, ook voor volgende keren</span>
          </label>
        ) : INLOG_AAN ? (
          <p className="text-[13px] text-ink-faint">Verhuisd? <a href="/inloggen" className="underline">Log in met je e-mailadres</a> om je adres ook voor volgende keren te wijzigen.</p>
        ) : null}
      </fieldset>
      <AdresVelden adres={adres} disabled={bezig} />
      {fout && <p className="text-body-sm text-red-700">{fout}</p>}
      <Knoppen bezig={bezig || adres.isLookingUp} onAnnuleer={onAnnuleer} />
    </form>
  )
}

function TelefoonBewerken({ token, huidig, onKlaar, onAnnuleer }: { token: string; huidig: string | null; onKlaar: (melding: string) => void; onAnnuleer: () => void }) {
  const [mobiel, setMobiel] = useState(huidig ?? '')
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)

  const opslaan = async () => {
    if (mobiel.trim() === (huidig ?? '').trim()) return onAnnuleer()
    setBezig(true)
    setFout(null)
    const r = await postMijnKleda({ token, actie: 'telefoon', mobiel: mobiel.trim() })
    setBezig(false)
    if (r.success && r.data && r.data.actie === 'telefoon') return onKlaar(`Geregeld. We bereiken je voortaan op ${r.data.mobiel}.`)
    setFout(r.error?.message || 'Dat lukte niet. Probeer het zo opnieuw of bel ons.')
  }

  return (
    <form className="mt-sm rounded-xl bg-surface-soft p-sm" onSubmit={(e) => { e.preventDefault(); void opslaan() }}>
      <label htmlFor="mk-mobiel" className="block text-body-sm text-ink-muted mb-xs">Mobiel nummer</label>
      <input id="mk-mobiel" type="tel" autoComplete="tel" value={mobiel} onChange={(e) => setMobiel(e.target.value)} placeholder="06 12345678" className="input w-full" disabled={bezig} />
      <p className="mt-xs text-[12px] text-ink-faint">Zo kan de chauffeur je bereiken als dat nodig is.</p>
      {fout && <p className="mt-xs text-body-sm text-red-700">{fout}</p>}
      <Knoppen bezig={bezig} onAnnuleer={onAnnuleer} />
    </form>
  )
}

function EmailBewerken({ token, huidig, onKlaar, onAnnuleer }: { token: string; huidig: string | null; onKlaar: (melding: string) => void; onAnnuleer: () => void }) {
  const [email, setEmail] = useState('')
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)

  const verstuur = async () => {
    if (!email.trim()) return
    setBezig(true)
    setFout(null)
    const r = await postMijnKleda({ token, actie: 'email-start', email: email.trim() })
    setBezig(false)
    if (r.success && r.data && r.data.actie === 'email-start') {
      return onKlaar(`We hebben een mail gestuurd naar ${r.data.nieuwEmail}. Klik daarin om te bevestigen; tot die tijd blijft ${huidig ?? 'je huidige adres'} staan.`)
    }
    setFout(r.error?.message || 'Dat lukte niet. Probeer het zo opnieuw of bel ons.')
  }

  return (
    <form className="mt-sm rounded-xl bg-surface-soft p-sm" onSubmit={(e) => { e.preventDefault(); void verstuur() }}>
      <label htmlFor="mk-nieuw-email" className="block text-body-sm text-ink-muted mb-xs">Nieuw e-mailadres</label>
      <input id="mk-nieuw-email" type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="naam@voorbeeld.nl" className="input w-full" disabled={bezig} />
      <p className="mt-xs text-[12px] text-ink-faint">We sturen een mail naar het nieuwe adres. Pas als je daarin klikt, gaat het over.</p>
      {fout && <p className="mt-xs text-body-sm text-red-700">{fout}</p>}
      <Knoppen bezig={bezig} onAnnuleer={onAnnuleer} label="Stuur bevestigingsmail" />
    </form>
  )
}

interface Props {
  token: string
  werkOrderId: string
  data: MijnKledaData
  /** Geslaagd: de ouder toont de melding en laadt de stand opnieuw. */
  onGewijzigd: (melding: string) => void
}

export function GegevensKaart({ token, werkOrderId, data, onGewijzigd }: Props) {
  const { klant } = data
  const g = data.gegevens
  const kan = g?.wijzigen === true
  const [bewerk, setBewerk] = useState<Veld | null>(null)

  const klaar = (veld: Veld, scope?: string) => (melding: string) => {
    setBewerk(null)
    trackGegevensGewijzigd(werkOrderId, veld, scope)
    onGewijzigd(melding)
  }
  const adresKanWijzigen = kan && !!(g?.orderAdres || g?.klantkaartAdres)
  const adresTekst = (kan && adresRegel(g?.orderAdres ?? g?.klantkaartAdres)) || klant.adres
  const telefoon = (kan ? g?.mobiel : null) ?? klant.telefoon

  return (
    <Card>
      <dl className="grid grid-cols-[110px_1fr] gap-y-xs text-body-sm">
        {klant.naam && (<><dt className="text-ink-faint">Naam</dt><dd className="text-ink">{klant.naam}</dd></>)}

        {(adresTekst || adresKanWijzigen) && (
          <>
            <dt className="text-ink-faint">Adres</dt>
            <dd className="text-ink">
              {adresTekst}
              {adresKanWijzigen && bewerk !== 'adres' && <WijzigLink onClick={() => setBewerk('adres')} />}
            </dd>
          </>
        )}
        {bewerk === 'adres' && (
          <div className="col-span-2">
            <AdresBewerken token={token} data={data} onAnnuleer={() => setBewerk(null)} onKlaar={(m, scope) => klaar('adres', scope)(m)} />
          </div>
        )}

        {(telefoon || kan) && (
          <>
            <dt className="text-ink-faint">Telefoon</dt>
            <dd className="text-ink">
              {telefoon ?? <span className="text-ink-faint">nog niet bekend</span>}
              {kan && bewerk !== 'telefoon' && <WijzigLink onClick={() => setBewerk('telefoon')} />}
            </dd>
          </>
        )}
        {bewerk === 'telefoon' && (
          <div className="col-span-2">
            <TelefoonBewerken token={token} huidig={g?.mobiel ?? null} onAnnuleer={() => setBewerk(null)} onKlaar={klaar('telefoon')} />
          </div>
        )}

        {klant.email && (
          <>
            <dt className="text-ink-faint">E-mail</dt>
            <dd className="text-ink break-all">
              {klant.email}
              {g?.emailWijzigen && bewerk !== 'email' && <WijzigLink onClick={() => setBewerk('email')} />}
              {g?.emailInBehandeling && (
                <span className="block text-[12px] text-ink-faint">
                  Wacht op je bevestiging: {g.emailInBehandeling}. Kijk in je mail, ook bij ongewenste mail.
                </span>
              )}
            </dd>
          </>
        )}
        {bewerk === 'email' && (
          <div className="col-span-2">
            <EmailBewerken token={token} huidig={klant.email} onAnnuleer={() => setBewerk(null)} onKlaar={klaar('email')} />
          </div>
        )}
      </dl>
      {kan && data.niveau === 'basis' && INLOG_AAN && !g?.orderAdres && (
        <p className="mt-sm text-[13px] text-ink-faint">
          Verhuisd? <a href="/inloggen" className="underline">Log in met je e-mailadres</a> om je adres hier te wijzigen.
        </p>
      )}
      <p className="mt-sm text-[13px] text-ink-faint">
        {kan ? 'Lukt het niet?' : 'Klopt er iets niet?'} Mail <a href={`mailto:${KLEDA_CONTACT.email}`} className="underline">{KLEDA_CONTACT.email}</a> of bel{' '}
        <a href={`tel:${KLEDA_CONTACT.telefoonTel}`} className="underline">{KLEDA_CONTACT.telefoonWeergave}</a>.
      </p>
    </Card>
  )
}
